import { useAppLayoutConfig } from '@src/providers/AppLayoutConfig/AppLayoutConfigContext';
import { Button, createBlotterTableNativeClickRef, IconButton, IconName, type ButtonProps } from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { getRFQSweepingRoute } from '../../../../containers/Routes/routes';
import { useCurrentAppLayoutId } from '../../../../hooks/useAppLayoutId';
import { getNavGroupSlug } from '../../../NavigationSidebar/spacesNavPath';

interface RFQSweepingButtonProps extends Omit<ButtonProps, 'onClick'> {
  correlationID: string;
  /** Render as the compact scale icon action used in quote-market rows. */
  onlyIcon?: boolean;
}

/** Pulls together the strings necessary to, given a correlationID, let you create and navigate to a dedicated sweeping panel for that correlation ID */
export function useOpenRFQSweepingPanel(correlationID: string) {
  const navigate = useNavigate();
  const layoutId = useCurrentAppLayoutId();
  const { layoutConfigs } = useAppLayoutConfig('trading');
  const navGroupName = useMemo(() => getNavGroupSlug(layoutConfigs, layoutId), [layoutConfigs, layoutId]);

  return useCallback(() => {
    navigate(getRFQSweepingRoute({ sweepGroupID: correlationID, layoutId, navGroupName }));
  }, [correlationID, layoutId, navGroupName, navigate]);
}

export function RFQSweepingButton({
  correlationID,
  onlyIcon = false,
  children = 'Sweep',
  disabled,
  ...buttonProps
}: RFQSweepingButtonProps) {
  const openRFQSweepingPanel = useOpenRFQSweepingPanel(correlationID);
  const setOpenButtonRef = createBlotterTableNativeClickRef<HTMLButtonElement>({
    onClick: openRFQSweepingPanel,
  });

  return onlyIcon ? (
    <IconButton {...buttonProps} icon={IconName.Scale} disabled={disabled} ref={setOpenButtonRef} />
  ) : (
    <Button {...buttonProps} disabled={disabled} ref={setOpenButtonRef}>
      {children}
    </Button>
  );
}
